import type { SubmitButtonProps } from '@mfw/forms';
import React from 'react';

import { type ButtonProps, Button } from '@mfw/components';
import { FormSubmitButton } from '@mfw/forms';
import Box from '@mui/material/Box';

export type PageActionButtonProps = ButtonProps;
export type PageActionsPosition = 'center' | 'end' | 'start';
export interface PageActionsProps {
  /**
   * Position of the actions.
   * @default start
   */
  position?: PageActionsPosition;
  children?: React.ReactNode;
  className?: string;
}

const positionMap: Record<PageActionsPosition, string> = {
  center: 'center',
  end: 'flex-end',
  start: 'flex-start',
};

const PageActions = ({ children, position = 'start' }: PageActionsProps) => (
  <Box
    justifyContent={{ md: positionMap[position], xs: 'center' }}
    width={{ md: '33.33%', xs: '100%' }}
    display="flex"
    pl={{ md: 4 }}
    gap={2}
  >
    {children}
  </Box>
);

/**
 * Every action below FORWARDS ITS REF, and that is not decoration.
 *
 * A `Tooltip` anchors to its child through a ref. Given a plain function
 * component it cannot: React warns "Function components cannot be given refs"
 * and the tooltip never renders — silently, since nothing throws. Both library
 * footers wrap `Archive` / `Unarchive` / `AssignTo` / `CopyTemplate` in one,
 * and below `lg` they collapse to ICON ONLY, so the tooltip is the only thing
 * naming those buttons on a tablet.
 *
 * `Button` and `FormSubmitButton` already forward; the ref was dying in these
 * one-line wrappers.
 */
const PageActionCancel = React.forwardRef<
  HTMLButtonElement,
  PageActionButtonProps
>((props, ref) => (
  <Button
    ref={ref}
    sx={{ width: 164 }}
    variant="outlined"
    size="medium"
    {...props}
  />
));

const PageActionSubmit = React.forwardRef<
  HTMLButtonElement,
  PageActionButtonProps & SubmitButtonProps
>((props, ref) => (
  <FormSubmitButton
    sx={{ minWidth: '164px !important', whiteSpace: 'nowrap' }}
    size="medium"
    ref={ref}
    {...props}
  />
));

const PageActionCopyTemplate = React.forwardRef<
  HTMLButtonElement,
  PageActionButtonProps
>((props, ref) => (
  <Button ref={ref} sx={{ width: 164 }} size="medium" {...props} />
));

const PageActionAssignTo = React.forwardRef<
  HTMLButtonElement,
  PageActionButtonProps
>((props, ref) => (
  <Button ref={ref} sx={{ width: 164 }} size="medium" {...props} />
));

const PageActionSaveAsNew = React.forwardRef<
  HTMLButtonElement,
  PageActionButtonProps
>((props, ref) => (
  <Button
    ref={ref}
    sx={{ width: 164 }}
    type="button"
    size="medium"
    {...props}
  />
));

const PageActionArchive = React.forwardRef<
  HTMLButtonElement,
  PageActionButtonProps
>((props, ref) => (
  <Button
    sx={{ width: 164 }}
    variant="outlined"
    color="error"
    size="medium"
    ref={ref}
    {...props}
  />
));

// bringing content back isn't destructive, so no error colouring here
const PageActionUnarchive = React.forwardRef<
  HTMLButtonElement,
  PageActionButtonProps
>((props, ref) => (
  <Button
    ref={ref}
    sx={{ width: 164 }}
    variant="outlined"
    size="medium"
    {...props}
  />
));

// Unlike Archive, this one does not come back — filled red, not outlined, so
// the two destructive actions never read as the same weight.
const PageActionDelete = React.forwardRef<
  HTMLButtonElement,
  PageActionButtonProps
>((props, ref) => (
  <Button
    sx={{ width: 164 }}
    variant="contained"
    color="error"
    size="medium"
    ref={ref}
    {...props}
  />
));

PageActionCancel.displayName = 'PageActionCancel';
PageActionSubmit.displayName = 'PageActionSubmit';
PageActionCopyTemplate.displayName = 'PageActionCopyTemplate';
PageActionAssignTo.displayName = 'PageActionAssignTo';
PageActionSaveAsNew.displayName = 'PageActionSaveAsNew';
PageActionArchive.displayName = 'PageActionArchive';
PageActionUnarchive.displayName = 'PageActionUnarchive';
PageActionDelete.displayName = 'PageActionDelete';
PageActions.Cancel = PageActionCancel;
PageActions.Submit = PageActionSubmit;
PageActions.CopyTemplate = PageActionCopyTemplate;
PageActions.AssignTo = PageActionAssignTo;
PageActions.SaveAsNew = PageActionSaveAsNew;
PageActions.Archive = PageActionArchive;
PageActions.Unarchive = PageActionUnarchive;
PageActions.Delete = PageActionDelete;

export default PageActions;
